import React, { useEffect, useState, useRef } from 'react';
import logoImage from '../assets/logo.png';
import { authJsonHeaders } from '../utils/authHeaders';

/**
 * Page d'impression / visionnage de la liste des dépenses client.
 * URL: /depenses-client/print?filterType=...&date=...&dateDebut=...&dateFin=...&mois=...&annee=...&client_id=...&preview=1
 */
export default function DepensesClientListPrintPage() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [depenses, setDepenses] = useState([]);
  const [previewMode, setPreviewMode] = useState(false);
  const printDoneRef = useRef(false);

  const formatMontant = (montant) => {
    const value = parseFloat(montant || 0);
    if (isNaN(value)) return '0,000 TND';
    return `${value.toLocaleString('fr-FR', { minimumFractionDigits: 3, maximumFractionDigits: 3 })} TND`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${day}/${m}/${y}`;
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const filterType = params.get('filterType') || 'mois';
    const jourMode = params.get('jourMode') || 'date';
    const date = params.get('date');
    const dateDebut = params.get('dateDebut');
    const dateFin = params.get('dateFin');
    const mois = params.get('mois');
    const annee = params.get('annee');
    const clientId = params.get('client_id');
    const isPreview = params.get('preview') === '1';
    setPreviewMode(isPreview);

    const run = async () => {
      try {
        let url = '';
        const q = new URLSearchParams();
        if (clientId) {
          q.append('client_id', clientId);
          if (filterType === 'jour' && jourMode === 'date' && date) q.append('date', date);
          else if (filterType === 'jour' && jourMode === 'periode' && dateDebut && dateFin) {
            q.append('date_debut', dateDebut);
            q.append('date_fin', dateFin);
          } else if (filterType === 'mois' && mois && annee) {
            q.append('mois', mois);
            q.append('annee', annee);
          } else if (filterType === 'annee' && annee) q.append('annee', annee);
          url = `/api/depenses/par-client?${q.toString()}`;
        } else {
          q.append('type', 'client');
          if (filterType === 'jour' && jourMode === 'date' && date) {
            q.append('date', date);
            url = `/api/depenses?${q.toString()}`;
          } else if (filterType === 'jour' && jourMode === 'periode' && dateDebut && dateFin) {
            q.append('date_debut', dateDebut);
            q.append('date_fin', dateFin);
            url = `/api/depenses/par-periode?${q.toString()}`;
          } else if (filterType === 'mois' && mois && annee) {
            q.append('mois', mois);
            q.append('annee', annee);
            url = `/api/depenses?${q.toString()}`;
          } else if (filterType === 'annee' && annee) {
            q.append('annee', annee);
            url = `/api/depenses?${q.toString()}`;
          } else {
            url = `/api/depenses?${q.toString()}`;
          }
        }

        const res = await fetch(url, { headers: authJsonHeaders() });
        const data = await res.json();
        const list = data.success ? (data.depenses || []) : [];
        setDepenses(list);
        setLoading(false);

        if (!isPreview) {
          setTimeout(() => doPrint(list, params), 100);
        }
      } catch (e) {
        setError(e.message || 'Erreur');
        setLoading(false);
      }
    };
    run();
  }, []);

  function doPrint(list, params) {
    if (printDoneRef.current) return;
    printDoneRef.current = true;

    const filterType = params.get('filterType') || 'mois';
    const now = new Date();
    const dateImpression = now.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
    const heureImpression = now.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });

    const rowsHtml = list.length > 0
      ? list.map((d) => {
          let libelleText = d.libelle || d.description || '-';
          libelleText = libelleText.replace(/^\[CGM PAYÉ\]\s*/, '[PAYÉ PAR CGM] ').replace(/^\[CGM\]\s*/, '[PAYÉ PAR CGM] ');
          const clientName = d.client || d.beneficiaire || '-';
          return `<tr><td>${formatDate(d.date || d.date_operation)}</td><td>${clientName}</td><td>${libelleText}</td><td>${formatMontant(d.montant)}</td></tr>`;
        }).join('')
      : '<tr><td colspan="4" style="text-align:center;color:#666;font-style:italic;">Aucune dépense pour la période</td></tr>';

    const total = list.reduce((s, d) => s + parseFloat(d.montant || 0), 0);
    const printContent = `<!DOCTYPE html>
<html>
<head><title>Liste des dépenses client</title>
<style>body{font-family:Arial,sans-serif;margin:12px;font-size:11px;} table{width:100%;border-collapse:collapse;} th,td{border:1px solid #000;padding:4px 6px;} thead th{font-weight:bold;background:#f9f9f9;} .total-row{font-weight:bold;background:#f9f9f9;}</style>
</head>
<body>
<div style="text-align:right;margin-bottom:10px;font-size:11px;font-weight:bold;">Date d'impression : ${dateImpression}, ${heureImpression}</div>
<div style="text-align:center;margin-bottom:10px;"><img src="${logoImage}" alt="Logo" style="height:50px;" /></div>
<h2 style="margin:12px 0;">Liste des dépenses client</h2>
<table><thead><tr><th>Date</th><th>Client</th><th>Libellé</th><th>Montant</th></tr></thead>
<tbody>${rowsHtml}</tbody>
<tfoot><tr class="total-row"><td colspan="3">TOTAL</td><td>${formatMontant(total)}</td></tr></tfoot>
</table>
</body>
</html>`;

    try {
      document.open();
      document.write(printContent);
      document.close();
      window.onload = () => { window.print(); };
    } catch (_) {
      const w = window.open('', '_blank');
      if (w) {
        w.document.write(printContent);
        w.document.close();
        w.onload = () => { w.print(); w.close(); };
      }
    }
  }

  if (loading) return <div className="container py-5">Préparation{previewMode ? ' de la prévisualisation' : ' de l\'impression'}...</div>;
  if (error) return <div className="container py-5 text-danger">{error}</div>;

  if (previewMode && !loading) {
    const total = depenses.reduce((s, d) => s + parseFloat(d.montant || 0), 0);
    return (
      <div className="container py-4">
        <style>{`@media print { .depenses-client-print-no-print { display: none !important; } }`}</style>
        <div className="depenses-client-print-no-print mb-3 d-flex gap-2">
          <button type="button" className="btn btn-primary" onClick={() => window.print()}>
            <i className="fas fa-print me-1"></i> Imprimer
          </button>
          <button type="button" className="btn btn-secondary" onClick={() => window.close()}>Fermer</button>
        </div>
        <h2 className="mb-3">Liste des dépenses client</h2>
        <div className="table-responsive">
          <table className="table table-bordered">
            <thead><tr><th>Date</th><th>Client</th><th>Libellé</th><th>Montant</th></tr></thead>
            <tbody>
              {depenses.length > 0 ? depenses.map((d, i) => {
                let libelleText = d.libelle || d.description || '-';
                libelleText = libelleText.replace(/^\[CGM PAYÉ\]\s*/, '[PAYÉ PAR CGM] ').replace(/^\[CGM\]\s*/, '[PAYÉ PAR CGM] ');
                return (
                  <tr key={d.id || i}>
                    <td>{formatDate(d.date || d.date_operation)}</td>
                    <td>{d.client || d.beneficiaire || '-'}</td>
                    <td>{libelleText}</td>
                    <td>{formatMontant(d.montant)}</td>
                  </tr>
                );
              }) : (
                <tr><td colSpan="4" className="text-center text-muted fst-italic">Aucune dépense pour la période</td></tr>
              )}
            </tbody>
            <tfoot><tr className="table-secondary fw-bold"><td colSpan="3">TOTAL</td><td>{formatMontant(total)}</td></tr></tfoot>
          </table>
        </div>
      </div>
    );
  }
  return null;
}
